<script setup lang="ts">
/**
 * Everything on /templates that comes after the gallery: what belongs in a
 * template, the honest comparison against Word / Google Docs / Canva, the price
 * ladder, a cover letter skeleton, the first-resume case and the web business
 * card.
 *
 * Each band ends in the link that matches its own subject rather than in one
 * closing banner — a visitor who came for a cover letter template should leave
 * on /ai-cover-letter, not on the generic sign-up.
 *
 * Prices are read from usePremiumPlans, never written into the translations.
 */
const { t } = useI18n()
const { exportCreditPrice, jobPackPrice, yearlyPrice, currency } = usePremiumPlans()

const anatomy = ['identity', 'contact', 'summary', 'experience', 'education', 'skills', 'extras'] as const
const breaks = ['header', 'columns', 'tables', 'icons', 'image'] as const
const tools = ['word', 'docs', 'canva', 'inmyresume'] as const
const coverSteps = ['open', 'proof', 'fit', 'close'] as const
const coverDonts = ['one', 'two', 'three'] as const
const swaps = ['projects', 'parttime', 'volunteer', 'selftaught'] as const
const cardPoints = ['link', 'qr', 'update', 'analytics'] as const

const tiers = computed(() => [
  { id: 'free', price: t('templatesPage.pricing.tiers.free.price'), to: '/free-cv-builder', highlight: false },
  { id: 'single', price: exportCreditPrice, to: '/pricing', highlight: false },
  { id: 'pack', price: jobPackPrice, to: '/pricing', highlight: true },
  { id: 'yearly', price: yearlyPrice, to: '/pricing', highlight: false },
])
</script>

<template>
  <div class="sofax">
    <!-- What belongs in a template, and what silently breaks one -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('templatesPage.anatomy.eyebrow')"
            :title="t('templatesPage.anatomy.title')"
            :text="t('templatesPage.anatomy.lead')"
            width="max-w-4xl"
          />

          <div class="grid grid-cols-1 items-start gap-10 lg:grid-cols-[1fr_minmax(0,0.85fr)] lg:gap-20">
            <ol class="flex flex-col gap-6">
              <li v-for="(section, index) in anatomy" :key="section" class="flex items-start gap-5">
                <span
                  class="mt-1 inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-ink !text-base !font-bold !text-white dark:bg-white dark:!text-ink"
                  aria-hidden="true"
                >{{ index + 1 }}</span>
                <div>
                  <div class="display-heading display-heading-5 mb-2">
                    {{ t(`templatesPage.anatomy.sections.${section}.title`) }}
                  </div>
                  <p class="text-base">{{ t(`templatesPage.anatomy.sections.${section}.text`) }}</p>
                </div>
              </li>
            </ol>

            <!-- Dark panel: the failures that make a pretty template useless -->
            <div class="rounded-[0.625rem] bg-ink p-[1.875rem] text-white dark:bg-gray-800">
              <div class="display-heading display-heading-4 mb-6 !text-white">
                {{ t('templatesPage.anatomy.breakTitle') }}
              </div>
              <ul class="flex flex-col gap-4">
                <li v-for="item in breaks" :key="item" class="flex items-start gap-3">
                  <i class="bi bi-exclamation-triangle-fill mt-1 shrink-0 text-lg text-lilac" />
                  <span class="!text-base !text-white/80">{{ t(`templatesPage.anatomy.breaks.${item}`) }}</span>
                </li>
              </ul>
              <div class="mt-8">
                <LocaleLink to="/ats-check" class="link !text-white">
                  {{ t('templatesPage.anatomy.atsLink') }} →
                </LocaleLink>
              </div>
              <div class="mt-4">
                <LocaleLink to="/ats-friendly-resume-template" class="link !text-white">
                  {{ t('templatesPage.anatomy.atsTemplateLink') }} →
                </LocaleLink>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Word / Google Docs / Canva, with a real "falls down when" column -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('templatesPage.tools.eyebrow')"
            :title="t('templatesPage.tools.title')"
            :text="t('templatesPage.tools.lead')"
            width="max-w-4xl"
          />

          <ul class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <li v-for="tool in tools" :key="tool" class="card h-full">
              <div class="display-heading display-heading-4 mb-6">
                {{ t(`templatesPage.tools.items.${tool}.name`) }}
              </div>
              <dl class="flex flex-col gap-5">
                <div>
                  <dt class="mb-1 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
                    <i class="bi bi-check-circle-fill shrink-0 text-lg text-accent" />
                    {{ t('templatesPage.tools.good') }}
                  </dt>
                  <dd class="text-base">{{ t(`templatesPage.tools.items.${tool}.good`) }}</dd>
                </div>
                <div>
                  <dt class="mb-1 flex items-center gap-2 text-base font-semibold text-ink dark:text-white">
                    <i class="bi bi-x-circle shrink-0 text-lg text-muted" />
                    {{ t('templatesPage.tools.bad') }}
                  </dt>
                  <dd class="text-base">{{ t(`templatesPage.tools.items.${tool}.bad`) }}</dd>
                </div>
              </dl>
            </li>
          </ul>

          <div class="mx-auto mt-10 max-w-3xl text-center lg:mt-14">
            <p class="text-lg">{{ t('templatesPage.tools.closing') }}</p>
            <div class="mt-8">
              <SofaxBtn to="/free-cv-builder" :label="t('templatesPage.tools.cta')" variant="accent" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- The price ladder, spelled out where the "free templates" queries land -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('templatesPage.pricing.eyebrow')"
            :title="t('templatesPage.pricing.title')"
            :text="t('templatesPage.pricing.lead')"
            width="max-w-4xl"
          />

          <ul class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <li
              v-for="tier in tiers"
              :key="tier.id"
              class="flex h-full flex-col rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800"
              :class="tier.highlight ? 'ring-2 ring-accent' : ''"
            >
              <div class="display-heading display-heading-5 mb-2">
                {{ t(`templatesPage.pricing.tiers.${tier.id}.name`) }}
              </div>
              <div class="display-heading display-heading-3 mb-5">{{ tier.price }}</div>
              <p class="!mb-6 text-base">{{ t(`templatesPage.pricing.tiers.${tier.id}.text`) }}</p>
              <div class="mt-auto">
                <SofaxArrowLink :to="tier.to" :label="t(`templatesPage.pricing.tiers.${tier.id}.cta`)" />
              </div>
            </li>
          </ul>

          <p class="mt-10 text-center text-sm text-muted dark:text-gray-400">{{ t(currency === 'CZK' ? 'templatesPage.pricing.noteCzk' : currency === 'PLN' ? 'templatesPage.pricing.notePln' : 'templatesPage.pricing.note') }}</p>
        </div>
      </div>
    </section>

    <!-- Cover letter skeleton: its own query, answered as a paragraph not a product -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('templatesPage.coverLetter.eyebrow')"
            :title="t('templatesPage.coverLetter.title')"
            :text="t('templatesPage.coverLetter.lead')"
            width="max-w-4xl"
          />

          <ol class="mx-auto flex max-w-4xl flex-col gap-6">
            <li v-for="step in coverSteps" :key="step" class="card">
              <div class="display-heading display-heading-5 mb-3">
                {{ t(`templatesPage.coverLetter.steps.${step}.title`) }}
              </div>
              <p class="!mb-5 text-base">{{ t(`templatesPage.coverLetter.steps.${step}.text`) }}</p>
              <figure class="border-l-2 border-accent pl-5">
                <figcaption class="mb-1 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
                  {{ t('templatesPage.coverLetter.exampleLabel') }}
                </figcaption>
                <blockquote class="text-base italic">
                  {{ t(`templatesPage.coverLetter.steps.${step}.example`) }}
                </blockquote>
              </figure>
            </li>
          </ol>

          <div class="mx-auto mt-10 max-w-4xl rounded-[0.625rem] bg-surface p-[1.875rem] dark:bg-gray-800">
            <div class="display-heading display-heading-5 mb-5">{{ t('templatesPage.coverLetter.dontTitle') }}</div>
            <ul class="flex flex-col gap-3">
              <li v-for="item in coverDonts" :key="item" class="flex items-start gap-3">
                <i class="bi bi-x-circle mt-0.5 shrink-0 text-xl text-muted" />
                <span class="text-base">{{ t(`templatesPage.coverLetter.donts.${item}`) }}</span>
              </li>
            </ul>
          </div>

          <div class="mx-auto mt-10 max-w-4xl text-center">
            <SofaxBtn to="/ai-cover-letter" :label="t('templatesPage.coverLetter.cta')" variant="accent" />
            <span class="mt-4 block text-sm text-muted dark:text-gray-400">{{ t('templatesPage.coverLetter.ctaNote') }}</span>
            <!-- The long version lives on /ai-cover-letter, not on the blog:
                 this skeleton is the summary, that page is the guide. -->
            <LocaleLink to="/ai-cover-letter" class="link mt-5 inline-block">
              {{ t('templatesPage.coverLetter.readMore') }} →
            </LocaleLink>
          </div>
        </div>
      </div>
    </section>

    <!-- First resume: same layout, different evidence -->
    <section class="surface-alt">
      <div class="section-space">
        <div class="shell">
          <SofaxSectionHeading
            :eyebrow="t('templatesPage.firstJob.eyebrow')"
            :title="t('templatesPage.firstJob.title')"
            :text="t('templatesPage.firstJob.lead')"
            width="max-w-4xl"
          />

          <h3 class="mb-8 text-center !text-2xl md:!text-3xl">{{ t('templatesPage.firstJob.swapTitle') }}</h3>

          <ul class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <li v-for="swap in swaps" :key="swap" class="h-full rounded-[0.625rem] bg-white p-[1.875rem] dark:bg-gray-800">
              <div class="display-heading display-heading-5 mb-3">
                {{ t(`templatesPage.firstJob.swaps.${swap}.title`) }}
              </div>
              <p class="text-base">{{ t(`templatesPage.firstJob.swaps.${swap}.text`) }}</p>
            </li>
          </ul>

          <div class="mx-auto mt-10 max-w-3xl text-center">
            <p>{{ t('templatesPage.firstJob.lengthNote') }}</p>
            <div class="mt-8">
              <SofaxBtn to="/free-cv-builder" :label="t('templatesPage.firstJob.cta')" variant="accent" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Business card: says plainly that this is not an Avery print sheet -->
    <section class="bg-white dark:bg-gray-950">
      <div class="section-space">
        <div class="shell">
          <div class="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-20">
            <div>
              <div class="eyebrow mb-5">{{ t('templatesPage.businessCard.eyebrow') }}</div>
              <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('templatesPage.businessCard.title') }}</h2>
              <p class="mt-5 text-lg">{{ t('templatesPage.businessCard.lead') }}</p>
              <div class="mt-8">
                <SofaxBtn to="/online-cv" :label="t('templatesPage.businessCard.cta')" variant="dark" />
              </div>
            </div>

            <ul class="flex flex-col gap-5">
              <li v-for="point in cardPoints" :key="point" class="flex items-start gap-4">
                <SofaxIconTile icon="bi-check-lg" round size="sm" />
                <p class="pt-2 text-base">{{ t(`templatesPage.businessCard.points.${point}`) }}</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
